import api from '@/store/api';
//#region : Toast Handling :
/**
 * Shows a toast message.
 * @param {Object} context - The context object containing state and commit function.
 * @param {string} message - The message to be displayed in the toast.
 */
const showToast = ({ state, commit }, message) => {
  if (state.toast.show) commit('hideToast');

  setTimeout(() => {
    commit('showToast', {
      color: 'black',
      message,
      timeout: 3000
    });
  });
};

/**
 * Shows an error toast message.
 * @param {Object} context - The Vuex context object.
 * @param {Error} error - The error object to display.
 */
const showError = ({ state, commit }, error) => {
  if (state.toast.show) commit('hideToast');

  setTimeout(() => {
    commit('showToast', {
      color: 'error',
      message: error.toString(),
      timeout: 10000
    });
  });
};

/**
 * Shows a success toast message.
 * @param {Object} context - The Vuex context object.
 * @param {string} message - The success message to display.
 */
const showSuccess = ({ state, commit }, message) => {
  if (state.toast.show) commit('hideToast');

  setTimeout(() => {
    commit('showToast', {
      color: 'success',
      message,
      timeout: 3000
    });
  });
};
//#endregion

//#region : Authentication :
/**
 * Logs in user based on input credentials
 * @param {*} param0
 * @param {*} payload
 * @returns
 */
const login = ({ dispatch, commit }, payload) => {
  commit('setAuthenticating', true);

  const url = '/user/login';
  const params = {
    email: payload.email,
    password: payload.password,
    rememberMe: payload.rememberMe
  };

  return api
    .post(url, params)
    .then(() => {
      commit('setAuthenticating', false);
      commit('setShowLoginDialog', false);
      dispatch('getUser');
    })
    .finally((error) => {
      commit('setAuthenticating', false);
      throw error;
    });
};
/**
 * Logs out the currently signed in user
 * @param {*} param0
 * @returns
 */
const logout = ({ dispatch, commit }) => {
  commit('setAuthenticating', true);

  const url = '/user/logout';
  const params = {};

  return api.post(url, params).then(() => {
    commit('setAuthenticating', false);
    dispatch('getUser');
  });
};
/**
 * Gets the currently signed in user from the API
 * @param {*} param0
 * @returns
 */
const getUser = ({ dispatch, commit }) => {
  commit('setAuthenticating', true);

  const url = '/user/currentUser';
  const params = {};

  return api.post(url, params).then((response) => {
    commit('setAuthenticating', false);
    commit('setUser', response.data);
    dispatch('configurator/getProjects', null, { root: true });
  });
};
/**
 * Sends a Sig response
 * @param {*} param0
 * @param {*} payload
 * @returns
 */
const sigRespond = ({ commit }, payload) => {
  const url = '/sig/respond';
  const params = payload;

  return api
    .post(url, params)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
};

/**
 * Registers a user.
 *
 * @param {Object} context - The Vuex context object.
 * @param {Object} payload - The payload containing user registration data.
 * @param {string} payload.email - The user's email address.
 * @param {string} payload.password - The user's password.
 * @param {string} payload.firstName - The user's first name.
 * @param {string} payload.lastName - The user's last name.
 * @returns {Promise} A promise that resolves to the response data from the server.
 * @throws {Error} If an error occurs during the registration process.
 */
const register = ({ commit }, payload) => {
  commit('setAuthenticating', true);

  const url = '/user/register';
  const params = {
    email: payload.email,
    password: payload.password,
    firstName: payload.firstName,
    lastName: payload.lastName
  };

  return api
    .post(url, params)
    .then((response) => {
      commit('setAuthenticating', false);
      commit('setShowLoginDialog', false);
      // fire GA4 event
      const loginData = {
        event: 'sign_up',
        user: {
          user_id: payload.email,
          user_email: payload.email,
          user_name: payload.email,
          sign_up_method: 'Email'
        }
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(loginData);
      //console.table(window.dataLayer);
      return response.data;
    })
    .catch((error) => {
      commit('setAuthenticating', false);
      throw error;
    });
};

/**
 * Verifies the email using the provided token.
 *
 * @param {string} token - The token used for email verification.
 * @returns {Promise} A promise that resolves to the response data.
 * @throws {Error} If an error occurs during the API call.
 */
const verifyEmail = ({ _ }, token) => {
  const url = `/user/confirmemail/${token}`;

  return api
    .get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
};

/**
 * Changes the user's password.
 *
 * @param {Object} _ - The context object.
 * @param {Object} payload - The payload object containing email, password, and newPassword.
 * @returns {Promise} A promise that resolves to the response data or rejects with an error.
 */
const changePassword = ({ _ }, payload) => {
  const url = '/user/changepassword';
  const params = {
    email: payload.email,
    password: payload.password,
    newPassword: payload.newPassword
  };

  return api
    .post(url, params)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      console.log('error');
      if (error.response && error.response.data) {
        return error.response.data;
      } else {
        return error;
      }
    });
};
/**
 * Sends a reset password request to the server.
 *
 * @param {Object} _ - The context object (ignored in this function).
 * @param {string} email - The email address for the password reset request.
 * @returns {Promise} A promise that resolves to the server response or rejects with an error.
 */
const resetPasswordRequest = ({ _ }, email) => {
  const url = `/user/resetpasswordrequest/${email}`;

  return api
    .post(url)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      console.log('error');
      if (error.response && error.response.data) {
        return error.response.data;
      } else {
        return error;
      }
    });
};
/**
 * Resets the user's password.
 *
 * @param {Object} _ - The context object.
 * @param {Object} payload - The payload object containing email, password, and token.
 * @param {string} payload.email - The user's email.
 * @param {string} payload.password - The new password.
 * @param {string} payload.token - The reset token.
 * @returns {Promise} A promise that resolves to the response data or rejects with an error.
 */
const resetPassword = ({ _ }, payload) => {
  const url = '/user/resetpassword';
  const params = {
    email: payload.email,
    newPassword: payload.password,
    resetToken: payload.token
  };

  return api
    .post(url, params)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      console.log('error');
      if (error.response && error.response.data) {
        return error.response.data;
      } else {
        return error;
      }
    });
};
//#endregion

export default {
  showToast,
  sigRespond,
  showError,
  showSuccess,
  login,
  logout,
  getUser,
  changePassword,
  register,
  verifyEmail,
  resetPasswordRequest,
  resetPassword
};
